import {
  ACADEMIC_TERMS,
  BackrsFormAutoComplete,
  BackrsFormField,
  graduationYearOptions,
  programStartYearOptions,
} from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useLazyFetchAutocompleteOptionsQuery } from '@backrs_ui/grow-ui/src/redux/onboarding/onboarding.api';

import { Box, MenuItem, SxProps, TextField, Theme } from '@mui/material';
import { ControllerRenderProps, FieldValues } from 'react-hook-form';

const STRINGS = appStrings.modules.survey.blueprintAccountFields;

const SCHOOLS_LOOKUP_ENDPOINT = '/profile/lookups/schools';

const GRADUATION_YEAR_OPTIONS = graduationYearOptions().map(String);
const START_YEAR_OPTIONS = programStartYearOptions().map(String);

type BlueprintAccountFieldsProps = Readonly<{
  zipCode: string;
  isHighSchool: boolean;
  school: string;
  graduationYear: string;
  startTerm: string;
  startYear: string;
  onZipChange: (value: string) => void;
  onSchoolChange: (value: string) => void;
  onGraduationYearChange: (value: string) => void;
  onStartTermChange: (value: string) => void;
  onStartYearChange: (value: string) => void;
  showZipError: boolean;
  isStudent: boolean;
}>;

export const BlueprintAccountFields = ({
  zipCode,
  isHighSchool,
  school,
  graduationYear,
  startTerm,
  startYear,
  onZipChange,
  onSchoolChange,
  onGraduationYearChange,
  onStartTermChange,
  onStartYearChange,
  showZipError,
  isStudent,
}: BlueprintAccountFieldsProps) => {
  // A program has to finish after it starts, so each year narrows the other's choices.
  const graduationYearChoices = GRADUATION_YEAR_OPTIONS.filter(
    (year) => !startYear || Number(year) > Number(startYear),
  );
  const startYearChoices = START_YEAR_OPTIONS.filter(
    (year) => !graduationYear || Number(year) < Number(graduationYear),
  );
  const [triggerFetchAutocomplete, { data: autocompleteData }] =
    useLazyFetchAutocompleteOptionsQuery();

  const schoolOptions =
    autocompleteData?.find((option) => option.endpoint === SCHOOLS_LOOKUP_ENDPOINT)?.values ?? [];

  const schoolField: BackrsFormField = {
    name: 'survey-school-input',
    label: STRINGS.schoolLabel,
    placeholder: STRINGS.schoolPlaceholder,
    type: 'text',
    autoCompleteEndpoint: SCHOOLS_LOOKUP_ENDPOINT,
    selectOptions: { type: 'SINGLE', renderAsSelect: true, options: [] },
    onInputChange: (input: string) =>
      triggerFetchAutocomplete({ endpoint: SCHOOLS_LOOKUP_ENDPOINT, searchTerm: input }),
  };

  const schoolControllerField = {
    name: 'survey-school-input',
    value: school,
    onChange: (value: string) => onSchoolChange(value ?? ''),
    onBlur: () => undefined,
    ref: () => undefined,
  } as unknown as ControllerRenderProps<FieldValues, string>;

  return (
    <Box sx={containerSx}>
      <TextField
        id="survey-zip-code-input"
        label={STRINGS.zipLabel}
        placeholder={STRINGS.zipPlaceholder}
        value={zipCode}
        onChange={(event) => onZipChange(event.target.value.replace(/\D/g, '').slice(0, 5))}
        error={showZipError}
        helperText={showZipError ? STRINGS.zipError : undefined}
        inputProps={{ inputMode: 'numeric', autoComplete: 'postal-code' }}
        fullWidth
      />
      {isStudent && (
        <BackrsFormAutoComplete
          isMultiple={false}
          autoCompleteOptions={schoolOptions}
          options={[]}
          field={schoolField}
          controllerField={schoolControllerField}
          groupedFields={{}}
          index={0}
          error={undefined}
          groupKey="survey-account-fields"
        />
      )}
      {isStudent && (
        <Box sx={startFieldsRowSx}>
          {!isHighSchool && (
            <TextField
              id="survey-start-term-select"
              label={STRINGS.startTermLabel}
              value={startTerm}
              onChange={(event) => onStartTermChange(event.target.value)}
              select
              fullWidth
              SelectProps={{ displayEmpty: true }}
            >
              <MenuItem id="survey-start-term-placeholder" value="" disabled>
                {STRINGS.startTermPlaceholder}
              </MenuItem>
              {ACADEMIC_TERMS.map((term) => (
                <MenuItem key={term} value={term} id={`survey-start-term-${term.toLowerCase()}`}>
                  {term}
                </MenuItem>
              ))}
            </TextField>
          )}
          <TextField
            id="survey-start-year-select"
            label={STRINGS.startYearLabel}
            value={startYear}
            onChange={(event) => onStartYearChange(event.target.value)}
            select
            fullWidth
            SelectProps={{ displayEmpty: true }}
          >
            <MenuItem id="survey-start-year-placeholder" value="" disabled>
              {STRINGS.startYearPlaceholder}
            </MenuItem>
            {startYearChoices.map((year) => (
              <MenuItem key={year} value={year} id={`survey-start-year-${year}`}>
                {year}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      )}
      {isStudent && (
        <TextField
          id="survey-graduation-year-select"
          label={STRINGS.graduationYearLabel}
          value={graduationYear}
          onChange={(event) => onGraduationYearChange(event.target.value)}
          select
          fullWidth
          SelectProps={{ displayEmpty: true }}
        >
          <MenuItem id="survey-graduation-year-placeholder" value="" disabled>
            {STRINGS.graduationYearPlaceholder}
          </MenuItem>
          {graduationYearChoices.map((year) => (
            <MenuItem key={year} value={year} id={`survey-graduation-year-${year}`}>
              {year}
            </MenuItem>
          ))}
        </TextField>
      )}
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-4xl'],
  width: '100%',
  mt: backrs.spacing['spacing-2xl'],
});

const startFieldsRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: backrs.spacing['spacing-lg'],
  width: '100%',
});
